import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, MessageCircle, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { LandingHero, THEMES } from "@/components/LandingHero";
import { ComplianceFooter } from "@/components/ComplianceBanner";
import { FEATURES, TOOLS, INCLUDED, FAQS, PRICE, PLAN_MSG, waLink } from "@/pages/Landing";

const up = { hidden: { opacity: 0, y: 28 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }) };
const In = ({ children, i, className, ...p }) => <motion.div variants={up} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} custom={i} className={className} {...p}>{children}</motion.div>;
const H2 = ({ t, k, children }) => (
  <div className="max-w-2xl">
    <div className="text-[11px] font-mono tracking-[0.3em]" style={{ color: t.a }}>{k}</div>
    <h2 className="tfr-display mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight" style={{ color: t.text }}>{children}</h2>
  </div>
);
const Banner = ({ t }) => (
  <button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="block w-full py-2 text-center text-[11px] sm:text-xs font-semibold tracking-[0.15em]" style={{ background: t.a, color: t.btnText }} data-testid="founding-banner">
    FOUNDING MEMBER OFFER · ONLY {PRICE.seats} SEATS · {PRICE.founding}/MONTH LOCKED FOR LIFE <span className="ml-2 underline underline-offset-4">CLAIM YOUR SEAT →</span>
  </button>
);

/* ---------- feature layouts ---------- */
const FeatRows = ({ t }) => (
  <div className="mt-16 space-y-24">
    {FEATURES.map((f, i) => (
      <In key={f.t} className={`grid md:grid-cols-12 gap-10 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`feature-${i}`}>
        <div className="md:col-span-7 overflow-hidden rounded-3xl p-px" style={{ background: `linear-gradient(135deg, ${t.a}, transparent 60%)` }}>
          <img src={f.img} alt={f.t} loading="lazy" className="w-full rounded-[23px] transition-transform duration-700 hover:scale-[1.03]" />
        </div>
        <div className="md:col-span-5">
          <div className="tfr-display text-7xl font-extrabold leading-none" style={{ color: `${t.a}33` }}>{String(i + 1).padStart(2, "0")}</div>
          <div className="mt-2 text-[11px] font-mono tracking-[0.25em]" style={{ color: t.a }}>{f.tag}</div>
          <h3 className="tfr-display mt-3 text-3xl font-bold" style={{ color: t.text }}>{f.t}</h3>
          <p className="mt-4 leading-relaxed" style={{ color: t.mute }}>{f.d}</p>
        </div>
      </In>
    ))}
  </div>
);
const SPANS = ["md:col-span-4 md:row-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-3", "md:col-span-3"];
const FeatBento = ({ t }) => (
  <div className="mt-14 grid md:grid-cols-6 md:auto-rows-[290px] gap-4">
    {FEATURES.map((f, i) => (
      <In key={f.t} i={i % 3} className={`group relative min-h-[300px] overflow-hidden rounded-[28px] bg-[#0B0B0F] ${SPANS[i]}`} data-testid={`feature-${i}`}>
        <img src={f.img} alt={f.t} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black via-black/80 to-transparent" />
        <div className="absolute bottom-0 p-7 text-white">
          <span className="rounded-full px-2.5 py-1 text-[10px] font-mono tracking-[0.2em]" style={{ background: t.a, color: t.btnText }}>{f.tag}</span>
          <h3 className="tfr-display mt-3 text-2xl font-bold">{f.t}</h3>
          <p className={`mt-2 text-sm text-white/70 ${i ? "line-clamp-3 max-w-sm" : "max-w-md"}`}>{f.d}</p>
        </div>
      </In>
    ))}
  </div>
);
const FeatSwitch = ({ t }) => {
  const [a, setA] = useState(0);
  return (
    <div className="mt-14 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5 border-t" style={{ borderColor: t.line }}>
        {FEATURES.map((f, i) => (
          <button key={f.t} onMouseEnter={() => setA(i)} onClick={() => setA(i)} className="block w-full border-b py-5 text-left" style={{ borderColor: t.line }} data-testid={`feature-${i}`}>
            <div className="flex items-baseline gap-4 font-mono">
              <span className="text-xs" style={{ color: a === i ? t.a : t.mute }}>[{String(i + 1).padStart(2, "0")}]</span>
              <span className="text-xl sm:text-2xl font-bold uppercase tracking-tight transition-colors" style={{ color: a === i ? t.text : t.mute }}>{f.t}</span>
            </div>
            {a === i && <p className="mt-3 pl-12 text-sm leading-relaxed" style={{ color: t.mute }}>{f.d}</p>}
          </button>
        ))}
      </div>
      <div className="lg:col-span-7 relative aspect-[3/2] overflow-hidden border lg:sticky lg:top-24" style={{ borderColor: t.line }}>
        <AnimatePresence mode="wait">
          <motion.img key={a} src={FEATURES[a].img} alt={FEATURES[a].t} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }} className="absolute inset-0 h-full w-full object-cover" />
        </AnimatePresence>
        <div className="absolute left-0 top-0 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em]" style={{ background: t.a, color: t.btnText }}>{FEATURES[a].tag}</div>
      </div>
    </div>
  );
};

/* ---------- tools layouts ---------- */
const ToolCards = ({ t }) => (
  <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
    {TOOLS.map(([Icon, n, d], i) => (
      <In key={n} i={i % 4} className="group rounded-2xl border p-6 backdrop-blur transition-transform hover:-translate-y-1" style={{ borderColor: t.line, background: t.panel }} data-testid={`tool-${i}`}>
        <span className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${t.a}1F` }}><Icon className="h-5 w-5" style={{ color: t.a }} /></span>
        <div className="mt-5 font-semibold" style={{ color: t.text }}>{n}</div>
        <div className="mt-1 text-sm" style={{ color: t.mute }}>{d}</div>
      </In>
    ))}
  </div>
);
const ToolRows = ({ t }) => (
  <div className="mt-10 divide-y border-y" style={{ borderColor: t.line }}>
    {TOOLS.map(([Icon, n, d], i) => (
      <div key={n} className="grid grid-cols-12 items-center py-5" style={{ borderColor: t.line }} data-testid={`tool-${i}`}>
        <span className="col-span-2 sm:col-span-1 font-mono text-sm" style={{ color: t.mute }}>{String(i + 1).padStart(2, "0")}</span>
        <span className="col-span-10 sm:col-span-5 flex items-center gap-3 tfr-display text-xl font-bold" style={{ color: t.text }}><Icon className="h-5 w-5" style={{ color: t.a }} />{n}</span>
        <span className="col-span-12 sm:col-span-6 mt-1 sm:mt-0" style={{ color: t.mute }}>{d}</span>
      </div>
    ))}
  </div>
);
const ToolGrid = ({ t }) => (
  <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 border-l border-t font-mono" style={{ borderColor: t.line }}>
    {TOOLS.map(([Icon, n, d], i) => (
      <div key={n} className="border-r border-b p-6 transition-colors hover:bg-white/[0.03]" style={{ borderColor: t.line }} data-testid={`tool-${i}`}>
        <div className="flex items-center justify-between"><Icon className="h-5 w-5" style={{ color: t.a }} /><span className="text-[10px]" style={{ color: t.mute }}>{String(i + 1).padStart(2, "0")}</span></div>
        <div className="mt-6 text-sm font-bold uppercase" style={{ color: t.text }}>{n}</div>
        <div className="mt-1 text-xs" style={{ color: t.mute }}>{d}</div>
      </div>
    ))}
  </div>
);

/* ---------- pricing + faq ---------- */
const Pricing = ({ t, square }) => (
  <section id="pricing" className="max-w-5xl mx-auto px-6 py-28">
    <div className={`p-px ${square ? "" : "rounded-[32px]"}`} style={{ background: `linear-gradient(135deg, ${t.a}, ${t.line} 50%, ${t.a})` }}>
      <div className={`grid md:grid-cols-2 gap-10 p-8 sm:p-12 ${square ? "" : "rounded-[31px]"}`} style={{ background: t.panel }} data-testid="plan-card">
        <div>
          <span className="inline-block px-3 py-1 text-[10px] font-mono tracking-[0.25em]" style={{ background: `${t.a}1F`, color: t.a }}>FOUNDING MEMBER · ONLY {PRICE.seats} SEATS</span>
          <div className="mt-6 flex items-baseline gap-4">
            <span className="tfr-display text-7xl font-extrabold tracking-tighter" style={{ color: t.text }} data-testid="plan-price">{PRICE.founding}</span>
            <span className="text-2xl line-through" style={{ color: t.mute }}>{PRICE.regular}</span>
          </div>
          <div className="mt-2 text-sm font-mono" style={{ color: t.mute }}>per month · locked for life</div>
          <a href={waLink(PLAN_MSG)} target="_blank" rel="noopener noreferrer" className={`mt-8 inline-flex items-center px-7 py-4 text-sm font-semibold transition-[filter] hover:brightness-110 ${square ? "" : "rounded-full"}`} style={{ background: t.a, color: t.btnText }} data-testid="plan-whatsapp">
            <MessageCircle className="mr-2 h-4 w-4" />Claim a founding seat on WhatsApp
          </a>
          <p className="mt-4 text-xs" style={{ color: t.mute }}>Analytics & research tool only — not SEBI-registered advice.</p>
        </div>
        <ul className="space-y-3.5 self-center">{INCLUDED.map((x) => <li key={x} className="flex gap-3 text-sm" style={{ color: t.text }}><Check className="h-5 w-5 shrink-0" style={{ color: t.a }} />{x}</li>)}</ul>
      </div>
    </div>
  </section>
);
const Faq = ({ t }) => (
  <section id="faq" className="max-w-7xl mx-auto px-6 pb-28 grid lg:grid-cols-12 gap-10">
    <div className="lg:col-span-4"><H2 t={t} k="QUESTIONS">Before you join</H2></div>
    <Accordion type="single" collapsible className="lg:col-span-8 border-t" style={{ borderColor: t.line }} data-testid="faq-list">
      {FAQS.map(([, q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-b" style={{ borderColor: t.line }}>
          <AccordionTrigger className="py-5 text-left text-lg font-semibold hover:no-underline" style={{ color: t.text }} data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="pb-5 text-base leading-relaxed" style={{ color: t.mute }}>{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);
const Final = ({ t }) => (
  <section className="max-w-7xl mx-auto px-6 pb-24">
    <div className="relative overflow-hidden rounded-[32px] px-8 py-16 sm:px-14" style={{ background: t.a }}>
      <h2 className="tfr-display max-w-2xl text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: t.btnText }}>Trade with the flow, not against it.</h2>
      <p className="mt-4 max-w-xl" style={{ color: t.btnText, opacity: 0.75 }}>Made by desk institutional flow traders, for traders who want the same view.</p>
      <a href={waLink("Hi TFR, I'd like access to the TFR Institutional Scanner.")} target="_blank" rel="noopener noreferrer" className="group mt-8 inline-flex items-center rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: t.btnText, color: t.a }} data-testid="footer-whatsapp">
        Talk to the desk <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
      </a>
    </div>
  </section>
);

const VARIANTS = {
  1: { theme: THEMES.navy, Feat: FeatRows, Tools: ToolCards },
  2: { theme: THEMES.ivory, Feat: FeatBento, Tools: ToolRows },
  3: { theme: THEMES.amber, Feat: FeatSwitch, Tools: ToolGrid, square: true },
};

export default function LandingPreview({ v }) {
  const navigate = useNavigate();
  const { theme: t, Feat, Tools, square } = VARIANTS[v];
  return (
    <div className="tfr-landing min-h-screen overflow-x-hidden" style={{ background: t.bg, color: t.text }} data-testid={`landing-preview-${v}`}>
      <Banner t={t} />
      <LandingHero theme={t} onSignIn={() => navigate("/login")} />
      <section id="features" className="max-w-7xl mx-auto px-6 py-28"><H2 t={t} k="PROPRIETARY ENGINES">Built on a trading desk. Not borrowed from a textbook.</H2><Feat t={t} /></section>
      <section id="tools" className="max-w-7xl mx-auto px-6 pb-12"><H2 t={t} k="THE FULL DESK">Everything on one screen</H2><Tools t={t} /></section>
      <Pricing t={t} square={square} />
      <Faq t={t} />
      <Final t={t} />
      <div className={t.light ? "" : "tfr-dark-footer"}><ComplianceFooter /></div>
    </div>
  );
}
